import React from "react";

/**
 * 搜索关键词高亮（需求 SYS-006 第 3 条：「搜索结果高亮匹配项」）。
 *
 * 为什么抽成公共模块：客户列表（CRMLeads）与顶部全局搜索（GlobalSearch）都要高亮，
 * 两个地方各自写一份正则很容易走样——一边能高亮电话、另一边高亮不了，用户会以为
 * 「同一个词在列表里能搜到、在 ⌘K 里搜不到」。这里只实现一次，两处共用。
 *
 * 电话容错（与后端 _crm_keyword_or 同口径）：
 *   库里的号码写法不统一（626-242-5879 / 6262425879 / 626 242 5879），后端为了能搜到
 *   已经把「纯数字输入」翻译成「任意分隔符」的匹配。高亮必须做同样的事，否则会出现
 *   「搜 6262425879 能搜到客户，但结果里那串 626-242-5879 一点都没标出来」的割裂感。
 *
 * 取舍：
 *   · 只做「原文 + 电话归一」两种模式，不做拼音 / 模糊纠错 —— 与后端召回口径保持一致，
 *     能高亮的必然是真命中，不会出现「高亮了却不知道凭什么命中」；
 *   · 正则元字符必须转义：用户可能输入 626.227 这类带点的词，不转义会变成通配符。
 */

const ESCAPE_RX = /[.*+?^${}()|[\]\\]/g;

/** 把一段文本按关键词切成 [{ t: 片段, hit: 是否命中 }]，未命中时返回单个 hit=false 片段。 */
export function highlightParts(text, keyword) {
  const s = String(text ?? "");
  const k = String(keyword || "").trim();
  if (!k || !s) return s ? [{ t: s, hit: false }] : [];

  const safe = k.replace(ESCAPE_RX, "\\$&");
  // 用户输入的是「像电话号码的一串字符」时，额外生成一条容错模式（数字之间允许任意分隔符）。
  // 阈值与后端一致：至少 3 位，且整串只能是数字与常见分隔符，避免把年份、金额当号码处理。
  const digits = k.replace(/\D/g, "");
  const phoneLike = /^[\d\s()+\-.]{3,}$/.test(k) && digits.length >= 3;
  const sources = [safe];
  if (phoneLike) {
    sources.push(digits.split("").map((d) => d.replace(ESCAPE_RX, "\\$&")).join("[\\s()+.\\-]*"));
  }

  let re;
  try {
    // 长的放前面：完整关键词优先于归一化后的号码片段，避免把同一段文字切得更碎。
    re = new RegExp(`(${sources.sort((a, b) => b.length - a.length).join("|")})`, "gi");
  } catch {
    return [{ t: s, hit: false }];
  }

  const parts = [];
  let last = 0;
  let m;
  while ((m = re.exec(s)) !== null) {
    if (m.index > last) parts.push({ t: s.slice(last, m.index), hit: false });
    if (m[0]) parts.push({ t: m[0], hit: true });
    last = m.index + m[0].length;
    if (m[0].length === 0) re.lastIndex += 1; // 防御空匹配死循环
  }
  if (!parts.length) return [{ t: s, hit: false }];
  if (last < s.length) parts.push({ t: s.slice(last), hit: false });
  return parts;
}

/**
 * 把文本渲染成带 <mark> 高亮的 React 节点。
 * 未命中时原样返回字符串（调用方可以直接 {highlightText(x, q)}，与旧实现完全兼容）。
 *
 * mark 的配色沿用客户列表既有样式（琥珀底 + 深色字），两处视觉一致；
 * 用 createElement 而不是 JSX，是为了让本模块保持 .js 后缀、便于直接用单测覆盖切词逻辑。
 */
export function highlightText(text, keyword, markClassName) {
  const parts = highlightParts(text, keyword);
  if (parts.length <= 1 && !parts[0]?.hit) return parts[0]?.t ?? "";
  return parts.map((p, i) =>
    p.hit
      ? React.createElement("mark", {
          key: `${i}-${p.t}`,
          className: markClassName || "bg-amber-200/70 text-slate-900 rounded-[2px] px-0.5",
        }, p.t)
      : p.t
  );
}

export default highlightText;
